/**
 * Chat Highlights
 *
 * Shared types and browser helpers for saved chat message highlights.
 *
 * Primary responsibilities:
 * - Define persisted highlight records
 * - Normalize API and cache data safely
 * - Read and write a browser fallback cache
 * - Call the authenticated chat highlights API
 * - Dispatch navigation events for saved highlights
 *
 * Dependencies:
 * - Browser CustomEvent, fetch, and localStorage APIs
 */

export const CHAT_HIGHLIGHT_OPEN_EVENT = 'losi:chat-highlight-open'
const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{12}$/i

export type ChatHighlight = {
  id: string
  chatId: string
  messageId: string
  text: string
  startOffset: number | null
  endOffset: number | null
  prefix: string
  suffix: string
  createdAt: string
}

export type ChatHighlightDraft = {
  messageId: string
  text: string
  startOffset: number | null
  endOffset: number | null
  prefix: string
  suffix: string
}

export type ChatHighlightOpenDetail = {
  highlightId: string
  messageId: string
}

// --------------

export function getChatHighlightsStorageKey(chatId: string) {
  return `losi:chat-highlights:${chatId}`
}

// --------------

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

// --------------

function normalizeOffset(value: unknown) {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0
    ? value
    : null
}

// --------------

function normalizeContext(value: unknown) {
  return typeof value === 'string' ? value : ''
}

// --------------

function normalizeHighlight(value: unknown): ChatHighlight | null {
  if (!value || typeof value !== 'object') return null

  const record = value as Record<string, unknown>
  const id = typeof record.id === 'string' ? record.id.trim() : ''
  const chatId = typeof record.chatId === 'string' ? record.chatId.trim() : ''
  const messageId =
    typeof record.messageId === 'string' ? record.messageId.trim() : ''
  const text = typeof record.text === 'string' ? record.text.trim() : ''
  const createdAt =
    typeof record.createdAt === 'string' && record.createdAt.trim()
      ? record.createdAt
      : new Date().toISOString()

  if (!id || !chatId || !messageId || !text) return null

  return {
    id,
    chatId,
    messageId,
    text,
    startOffset: normalizeOffset(record.startOffset),
    endOffset: normalizeOffset(record.endOffset),
    prefix: normalizeContext(record.prefix),
    suffix: normalizeContext(record.suffix),
    createdAt
  }
}

// --------------

export function normalizeChatHighlights(value: unknown) {
  if (!Array.isArray(value)) return []

  return value.flatMap((item) => {
    const highlight = normalizeHighlight(item)
    return highlight ? [highlight] : []
  })
}

// --------------

function getApiErrorMessage(body: Record<string, unknown>, fallback: string) {
  return typeof body.error === 'string' && body.error.trim()
    ? body.error
    : fallback
}

// --------------

async function readApiBody(response: Response) {
  const body = (await response.json().catch((): unknown => ({}))) as unknown

  return isRecord(body) ? body : {}
}

// --------------

export function readCachedChatHighlights(chatId: string) {
  if (typeof window === 'undefined') return []

  try {
    const rawHighlights = window.localStorage.getItem(
      getChatHighlightsStorageKey(chatId)
    )
    const parsedHighlights = rawHighlights ? JSON.parse(rawHighlights) : []

    return normalizeChatHighlights(parsedHighlights).filter(
      (highlight) => highlight.chatId === chatId
    )
  } catch {
    return []
  }
}

// --------------

export function writeCachedChatHighlights(
  chatId: string,
  highlights: ChatHighlight[]
) {
  if (typeof window === 'undefined') return

  try {
    window.localStorage.setItem(
      getChatHighlightsStorageKey(chatId),
      JSON.stringify(highlights)
    )
  } catch {
    // Cache writes are best-effort; Supabase is the durable source.
  }
}

// --------------

export async function fetchChatHighlights(chatId: string) {
  const response = await fetch(
    `/api/chat-highlights?chatId=${encodeURIComponent(chatId)}`,
    {
      cache: 'no-store',
      credentials: 'same-origin'
    }
  )
  const body = await readApiBody(response)

  if (!response.ok) {
    throw new Error(getApiErrorMessage(body, 'Failed to load chat highlights'))
  }

  return normalizeChatHighlights(body.highlights)
}

// --------------

export async function saveChatHighlight(
  chatId: string,
  draft: ChatHighlightDraft
) {
  const response = await fetch('/api/chat-highlights', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ chatId, ...draft })
  })
  const body = await readApiBody(response)

  if (!response.ok) {
    throw new Error(getApiErrorMessage(body, 'Failed to save chat highlight'))
  }

  const [highlight] = normalizeChatHighlights([body.highlight])

  if (!highlight) {
    throw new Error('Saved highlight response was invalid')
  }

  return highlight
}

// --------------

export async function deleteChatHighlight(chatId: string, highlightId: string) {
  if (!UUID_PATTERN.test(highlightId)) return

  const params = new URLSearchParams({ chatId, highlightId })
  const response = await fetch(`/api/chat-highlights?${params.toString()}`, {
    method: 'DELETE',
    cache: 'no-store',
    credentials: 'same-origin'
  })
  const body = await readApiBody(response)

  if (response.status === 404) {
    return
  }

  if (!response.ok) {
    throw new Error(getApiErrorMessage(body, 'Failed to delete chat highlight'))
  }
}

// --------------

export function dispatchChatHighlightOpen(detail: ChatHighlightOpenDetail) {
  if (typeof window === 'undefined') return

  window.dispatchEvent(
    new CustomEvent<ChatHighlightOpenDetail>(CHAT_HIGHLIGHT_OPEN_EVENT, {
      detail
    })
  )
}
